import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Platform, Pressable } from 'react-native';

import { avatarDefault } from '@/assets/images';
import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import { Avatar, FormInput, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { GuardianProfileEditForm as FormValues } from '@/features/guardian-profile/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { MAX_CITY_LENGTH, MAX_EMAIL_LENGTH, MAX_PHONE_LENGTH } from '@/validation';

type Props = { tablet?: boolean; onPickAvatar: () => void; onDeleteAccount?: () => void };

const fieldProps = {
  uppercaseLabel: false,
  labelGap: 4,
  labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold' as const,
};

const PROFILE_NAME_MAX_LENGTH = 50;
const PROFILE_ZIP_MAX_LENGTH = 10;
const requiredRule = (value: string) =>
  value.trim().length > 0 || STRINGS.guardianProfile.edit.required;

export function ProfileEditForm({ tablet = false, onPickAvatar, onDeleteAccount }: Props) {
  const { scale } = useScaling();
  const styles = useStyles();
  const { control, setValue } = useFormContext<FormValues>();
  const countryCode = useWatch({ control, name: 'country' });
  const firstName = useWatch({ control, name: 'firstName' });
  const lastName = useWatch({ control, name: 'lastName' });
  const addressFieldProps = {
    ...fieldProps,
    triggerVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    inputVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    triggerStyle: [styles.fieldBorder, styles.webSelectTrigger],
    contentStyle: styles.fieldBorder,
  };

  return (
    <Stack gap={scale({ phone: 20, tablet: 20 })} width="100%">
      <Controller
        name="avatar"
        control={control}
        render={({ field: { value } }) =>
          tablet ? (
            <Stack direction="row" align="center" gap={scale(20)}>
              <Avatar
                onPress={onPickAvatar}
                size={scale(60)}
                borderWidth={scale(2)}
                borderColor={colors['0']}>
                <Avatar.Image
                  key={value?.uri ?? 'default-avatar'}
                  source={value?.uri ? { uri: value.uri } : avatarDefault}
                />
              </Avatar>
              <Pressable onPress={onPickAvatar} style={({ pressed }) => pressed && styles.pressed}>
                <Stack gap={scale(8)}>
                  <Stack direction="row" align="center" gap={scale(8)}>
                    <Text variant="Font Inter/Subtitle/Medium 12px" style={styles.uploadText}>
                      {STRINGS.guardianProfile.edit.uploadPhoto}
                    </Text>
                    <SvgIcon name="IcChevronRight" size={scale(16)} color={colors.Blue} />
                  </Stack>
                  <Text variant="Font Inter/Subtitle/Italic" fontStyle="italic" style={styles.hint}>
                    {STRINGS.guardianProfile.edit.photoHint}
                  </Text>
                </Stack>
              </Pressable>
            </Stack>
          ) : (
            <Stack align="center" gap={scale(8)} width="100%">
              <Pressable onPress={onPickAvatar}>
                <Avatar size={scale(100)} borderWidth={scale(2)} borderColor={colors['0']}>
                  <Avatar.Image
                    key={value?.uri ?? 'default-avatar'}
                    source={value?.uri ? { uri: value.uri } : avatarDefault}
                  />
                </Avatar>
                <Stack align="center" justify="center" style={styles.cameraBadge}>
                  <SvgIcon name="IcCamera" size={scale(16)} color={guardianColors['Main/Main']} />
                </Stack>
              </Pressable>
              <Text
                variant="Font Guardian App/Heading/Heading H6 18px"
                style={styles.phoneNamePreview}>
                {STRINGS.guardianProfile.edit.namePreview(firstName, lastName)}
              </Text>
            </Stack>
          )
        }
      />

      <Stack gap={scale(12)}>
        <Stack direction="row" gap={scale({ phone: 12, tablet: 16 })}>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="firstName"
              control={control}
              rules={{ validate: requiredRule }}
              render={({ field, fieldState }) => (
                <FormInput
                  {...fieldProps}
                  label={STRINGS.guardianProfile.edit.firstName}
                  required
                  value={field.value}
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  maxLength={PROFILE_NAME_MAX_LENGTH}
                  status={fieldState.invalid ? 'error' : 'default'}
                  errorMessage={fieldState.error?.message}
                  contentStyle={fieldState.invalid ? undefined : styles.fieldBorder}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="lastName"
              control={control}
              rules={{ validate: requiredRule }}
              render={({ field, fieldState }) => (
                <FormInput
                  {...fieldProps}
                  label={STRINGS.guardianProfile.edit.lastName}
                  required
                  value={field.value}
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  maxLength={PROFILE_NAME_MAX_LENGTH}
                  status={fieldState.invalid ? 'error' : 'default'}
                  errorMessage={fieldState.error?.message}
                  contentStyle={fieldState.invalid ? undefined : styles.fieldBorder}
                />
              )}
            />
          </Stack>
        </Stack>
        <Controller
          name="phone"
          control={control}
          rules={{ validate: requiredRule }}
          render={({ field, fieldState }) => (
            <FormInput
              {...fieldProps}
              label={STRINGS.guardianProfile.edit.phone}
              required
              phoneNumber
              phoneDivider
              phoneDividerStyle={styles.phoneDivider}
              phoneDefaultCountry="US"
              value={field.value}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              maxLength={MAX_PHONE_LENGTH}
              keyboardType="phone-pad"
              status={fieldState.invalid ? 'error' : 'default'}
              errorMessage={fieldState.error?.message}
              contentStyle={fieldState.invalid ? undefined : styles.fieldBorder}
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormInput
              {...fieldProps}
              label={STRINGS.guardianProfile.edit.email}
              required
              disabled
              value={field.value}
              maxLength={MAX_EMAIL_LENGTH}
              disableStyle={styles.disabledInput}
              contentStyle={styles.fieldBorder}
            />
          )}
        />
        <Stack direction="row" gap={scale({ phone: 12, tablet: 16 })}>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <CountryRegionField
                  {...addressFieldProps}
                  required={false}
                  label={STRINGS.guardianProfile.edit.country}
                  value={field.value}
                  onValueChange={value => {
                    field.onChange(value);
                    setValue('state', '', { shouldDirty: true });
                  }}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <StateProvinceField
                  {...addressFieldProps}
                  required={false}
                  label={STRINGS.guardianProfile.edit.state}
                  country={countryCode}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction="row" gap={scale({ phone: 12, tablet: 16 })}>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <FormInput
                  {...fieldProps}
                  label={STRINGS.guardianProfile.edit.city}
                  required={false}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  maxLength={MAX_CITY_LENGTH}
                  contentStyle={styles.fieldBorder}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <FormInput
                  {...fieldProps}
                  label={STRINGS.guardianProfile.edit.zipCode}
                  required={false}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChangeText={value => field.onChange(value.replace(/[^0-9]/g, ''))}
                  maxLength={PROFILE_ZIP_MAX_LENGTH}
                  keyboardType="numeric"
                  contentStyle={styles.fieldBorder}
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>

      <Pressable
        onPress={onDeleteAccount}
        style={({ pressed }) => [styles.deleteButton, pressed && styles.pressed]}>
        <SvgIcon name="IcDelete" size={scale(20)} color={guardianColors.Error} />
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.deleteText}>
          {STRINGS.guardianProfile.edit.deleteAccount}
        </Text>
      </Pressable>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  uploadText: { color: colors.Blue },
  hint: { color: guardianColors['6'] },
  phoneNamePreview: { width: '100%', textAlign: 'center' },
  phoneDivider: {
    marginRight: scale(8),
  },
  cameraBadge: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: scale(24),
    height: scale(24),
    borderRadius: scale(12),
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
  disabledInput: { backgroundColor: guardianColors['1'] },
  fieldBorder: {
    borderWidth: scale(1),
    borderColor: guardianColors['3'],
  },
  webSelectTrigger: Platform.select({
    web: { paddingLeft: scale(6) },
    default: {},
  }),
  deleteButton: {
    height: scale(48),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    marginTop: scale({ phone: 0, tablet: 4 }),
  },
  deleteText: { color: guardianColors.Error },
  pressed: { opacity: 0.68 },
}));
